import type { QueryClient } from "@tanstack/react-query";
import { apiService, type Company } from "../services/api";
import {
  readCompanyProductsCache,
  writeCompanyProductsCache,
} from "./companyProductsCache";

export type SyncCompanyProductsResult = {
  products: string[];
  enablePerUserProductPermissions: boolean;
};

/**
 * Seeds `['my-company', companyId]` in React Query + optional localStorage cache.
 * Used on login and session restore (not the periodic poll — see useAuthSyncQuery).
 */
export async function syncCompanyProducts(
  queryClient: QueryClient,
  userCompanyId: string | undefined,
  forceRefresh: boolean,
): Promise<SyncCompanyProductsResult> {
  if (!userCompanyId) {
    return { products: [], enablePerUserProductPermissions: false };
  }

  if (!forceRefresh) {
    const cached = readCompanyProductsCache(userCompanyId);
    if (cached !== null) {
      queryClient.setQueryData<Company>(["my-company", userCompanyId], (prev: Company | undefined) => ({
        ...(prev ?? ({} as Company)),
        products: cached.products,
        enablePerUserProductPermissions: cached.enablePerUserPermissions,
        dateFilterMinStartDate: cached.dateFilterMinStartDate ?? null,
        dateFilterMaxEndDate: cached.dateFilterMaxEndDate ?? null,
        miContenidoDateFilterMinStartDate:
          cached.miContenidoDateFilterMinStartDate ?? null,
        miContenidoDateFilterMaxEndDate:
          cached.miContenidoDateFilterMaxEndDate ?? null,
        trackerBrandId: cached.trackerBrandId ?? null,
        explorerSponsorCategoryFilter:
          cached.explorerSponsorCategoryFilter ?? null,
      }));
      return {
        products: cached.products,
        enablePerUserProductPermissions: cached.enablePerUserPermissions,
      };
    }
  }

  try {
    const company = await apiService.getMyCompany();
    const products = company.products || [];
    const enablePerUser = company.enablePerUserProductPermissions || false;
    const dateMin = company.dateFilterMinStartDate ?? null;
    const dateMax = company.dateFilterMaxEndDate ?? null;
    const miDateMin = company.miContenidoDateFilterMinStartDate ?? null;
    const miDateMax = company.miContenidoDateFilterMaxEndDate ?? null;
    const trackerBrandId = company.trackerBrandId ?? null;
    const explorerSponsorCategoryFilter =
      company.explorerSponsorCategoryFilter ?? null;

    writeCompanyProductsCache(
      userCompanyId,
      products,
      enablePerUser,
      dateMin,
      dateMax,
      miDateMin,
      miDateMax,
      trackerBrandId,
      explorerSponsorCategoryFilter,
    );
    queryClient.setQueryData<Company>(["my-company", userCompanyId], company);

    return { products, enablePerUserProductPermissions: enablePerUser };
  } catch (error) {
    console.error("Error fetching company products:", error);
    return { products: [], enablePerUserProductPermissions: false };
  }
}
